import { signal, Signal, computed, isDevMode } from '@angular/core';

/**
 * Abstract base class for state services.
 * Provides a typed signal, key-based selection, and automatic logging in dev mode.
 *
 * @example
 * ```typescript
 * interface IMyState { count: number; name: string; }
 *
 * @Injectable({ providedIn: 'root' })
 * export class MyStateService extends StateService<IMyState> {
 *   protected readonly stateName = 'MyState';
 *   constructor() {
 *     super({ count: 0, name: '' });
 *   }
 * }
 * ```
 */
export abstract class StateService<T> {
  /** State name used to identify it in console logs. */
  protected abstract readonly stateName: string;

  readonly state: ReturnType<typeof signal<T>>;

  constructor(initialState: T) {
    this.state = signal(initialState);
  }

  /**
   * Selects a state property as a read-only signal.
   * @param key - State key to select
   */
  public select<K extends keyof T>(key: K): Signal<T[K]> {
    return computed(() => this.state()[key]);
  }

  /**
   * Partially updates the state.
   * In dev mode, logs the resulting state to the console.
   * @param partialState - Properties to update
   */
  public setState(partialState: Partial<T>): void {
    this.state.update((current) => {
      const newState = { ...current, ...partialState };

      if (isDevMode()) {
        console.log(`%c[${this.stateName}]`, 'color: #7c3aed; font-weight: bold;', newState);
      }

      return newState;
    });
  }
}
